import { useMemo } from "react";
import { INTERESTS } from "@/lib/api";

const DAYS = 30;

const dayKey = (d) => d.toLocaleDateString("en-CA");

const Stat = ({ label, value, testId }) => (
  <div className="tn-card p-5">
    <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--tn-muted)]">{label}</div>
    <div className="font-display text-3xl font-semibold mt-2" data-testid={testId}>{value}</div>
  </div>
);

const Daily = ({ series }) => {
  const max = Math.max(1, ...series.map((s) => s.n));
  return (
    <div className="tn-card p-6" data-testid="analytics-daily-chart">
      <h3 className="font-display font-medium">Waitlist sign-ups per day <span className="text-[var(--tn-muted)] text-sm font-normal">· last {DAYS} days</span></h3>
      <div className="flex items-end gap-1 h-40 mt-6">
        {series.map((s) => (
          <div key={s.day} className="flex-1 h-full flex flex-col justify-end group relative">
            <div className="bg-[var(--tn-cyan)] rounded-t-sm min-h-[2px] transition-opacity group-hover:opacity-70" style={{ height: `${(s.n / max) * 100}%`, opacity: s.n ? 1 : 0.2 }} />
            <span className="absolute -top-6 left-1/2 -translate-x-1/2 font-mono text-[10px] bg-[var(--tn-bg)] border border-[var(--tn-line)] px-1.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none">{s.day.slice(5)}: {s.n}</span>
          </div>
        ))}
      </div>
      <div className="flex justify-between font-mono text-[10px] text-[var(--tn-muted)] mt-2">
        <span>{series[0].day}</span><span>{series[series.length - 1].day}</span>
      </div>
    </div>
  );
};

const Breakdown = ({ rows }) => {
  const total = rows.length || 1;
  return (
    <div className="tn-card p-6" data-testid="analytics-interest-breakdown">
      <h3 className="font-display font-medium">Interest breakdown</h3>
      <ul className="space-y-4 mt-6">
        {INTERESTS.map((i, idx) => {
          const n = rows.filter((r) => r.interest === i).length;
          return (
            <li key={i} data-testid={`analytics-interest-${idx}`}>
              <div className="flex justify-between text-sm"><span>{i}</span><span className="font-mono text-[var(--tn-muted)]">{n} · {Math.round((n / total) * 100)}%</span></div>
              <div className="h-1.5 rounded-full bg-[var(--tn-line)] mt-2 overflow-hidden">
                <div className="h-full bg-[var(--tn-green)] rounded-full" style={{ width: `${(n / total) * 100}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const Analytics = ({ waitlist, ra }) => {
  const series = useMemo(() => {
    const counts = {};
    waitlist.forEach((r) => { const k = dayKey(new Date(r.created_at)); counts[k] = (counts[k] || 0) + 1; });
    return Array.from({ length: DAYS }, (_, i) => {
      const d = new Date(); d.setDate(d.getDate() - (DAYS - 1 - i));
      const day = dayKey(d);
      return { day, n: counts[day] || 0 };
    });
  }, [waitlist]);
  const today = series[series.length - 1].n;
  const week = series.slice(-7).reduce((a, s) => a + s.n, 0);

  return (
    <div className="space-y-5" data-testid="admin-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <Stat label="Waitlist total" value={waitlist.length} testId="analytics-total" />
        <Stat label="Today" value={today} testId="analytics-today" />
        <Stat label="Last 7 days" value={week} testId="analytics-week" />
        <Stat label="RA applications" value={ra.length} testId="analytics-ra-total" />
      </div>
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5">
        <Daily series={series} />
        <Breakdown rows={waitlist} />
      </div>
    </div>
  );
};
